import {
  Component, ElementRef, EventEmitter, Input, NgZone, OnInit, Output,
  ViewChild,
} from '@angular/core';
import { Campo } from 'src/app/models/coleta';
import { HandshakeService } from 'src/app/services/handshake.service';
import { take } from 'rxjs/operators';
import { FortFaceData } from 'src/app/models/fortface-data';
import { SessionStorageService } from 'src/app/services/session-storage.service';

type IFortfaceSessionDetails = {
    errorCode?: FortFaceSdk.FortfaceSessionErrorCode | string;
    metrics?: FortFaceSdk.IFortfaceReturnMetrics;
} | undefined;

/**
 * Mensagens amigáveis em PT-BR para os códigos de erro retornados pelo
 * callback do startSession (v2.4.0+).
 */
const SESSION_ERROR_MESSAGES: Record<string, string> = {
  notInitialized: 'A captura não foi inicializada corretamente. Tente novamente.',
  cameraDisconnected: 'A câmera foi desconectada durante a captura. Verifique sua câmera e tente novamente.',
  noCompatibleVideoDevice: 'Nenhuma câmera compatível foi encontrada no dispositivo.',
  cameraPermissionDenied: 'Permissão de acesso à câmera negada. Habilite o acesso à câmera nas configurações do navegador.',
  cameraUnavailable: 'A câmera está indisponível. Verifique se ela não está sendo usada por outro aplicativo.',
  cameraAccessPolicyRestricted: 'Acesso à câmera bloqueado por política de segurança do navegador.',
  cameraAccessUnexpectedError: 'Erro inesperado ao acessar a câmera. Tente novamente.',
  browserUnsupported: 'Seu navegador não é compatível com a captura de biometria facial. Atualize ou use outro navegador.',
  browserValidationUnexpectedError: 'Erro inesperado ao validar o navegador. Tente novamente.'
};

/**
 * Mensagens amigáveis em PT-BR para os erros lançados pelo método start()
 * (v2.4.0+).
 */
const START_ERROR_MESSAGES: Record<string, string> = {
  FORTFACE_INSECURE_PROTOCOL: 'A captura de biometria exige uma conexão segura (HTTPS).',
  FORTFACE_GENERATE_DEVICE_REQUEST_INFO_ERROR: 'Falha ao preparar a captura de biometria. Tente novamente.',
  FORTFACE_INTERNAL_SETUP_ERROR: 'Falha ao inicializar a captura de biometria. Tente novamente.',
  FORTFACE_UNEXPECTED_START_ERROR: 'Erro inesperado ao iniciar a captura de biometria. Tente novamente.'
};

@Component({
  selector: 'app-image-capture-fortface',
  templateUrl: './image-capture-fortface.component.html',
  styleUrls: ['./image-capture-fortface.component.scss']
})
export class ImageCaptureFortFaceComponent implements OnInit {

  @Output()
  imageCapture = new EventEmitter<FortFaceData>();

  @Output()
  captureError = new EventEmitter<string>();

  @Input() isSelf: Boolean = false;

  @Input() exibirTelaCaptura: Boolean = false;

  @Input() campo: Campo = {};

  @Output()
  isLoading = new EventEmitter<Boolean>();

  @ViewChild('forfacesdk', { static: true }) sdk: ElementRef<FortFaceSdk.Components.FortfaceSdk> | undefined;

  private sessionId: string = "";

  constructor(private handshakeService: HandshakeService, private ngZone: NgZone, private sessionStorageService: SessionStorageService) {
  }

  async ngOnInit() {
    await this.initializeFortfaceSdk();
  }

  async initializeFortfaceSdk() {
    if (!this.sdk) return;

    this.ngZone.runOutsideAngular(async () => {
      try {
        // A partir da v2.4.0, o start() não solicita permissão de câmera nem
        // valida o navegador — isso é feito apenas no startSession(). Por isso
        // o getUserMedia manual foi removido daqui.
        const deviceRequestInfo = await this.sdk!.nativeElement.start();

        const fortfaceAction = this.sessionStorageService.getEnvConfig()?.fortfaceAction;

        this.handshakeService.performHandshake(deviceRequestInfo, fortfaceAction)
          .pipe(take(1))
          .subscribe(
            (response) => {
              this.ngZone.run(() => {
                if (!this.sdk) throw new Error("SDK not found");
                this.sessionId = response.sessionId;
                this.sdk.nativeElement.startSession(
                  this.fortfaceFinishSession.bind(this),
                  response.sessionId,
                  response.sessionKey
                );
              });
            },
            (error) => {
              this.ngZone.run(() => {
                this.captureError.emit("Oopss! Ocorreu um erro no servidor, tente novamente.");
                console.error('Erro ao chamar a API:', error);
              });
            }
          );
      } catch (error: any) {
        this.ngZone.run(() => {
          this.handleStartError(error);
        });
      }
    });
  }

  /**
   * Trata os erros lançados pelo método start() (v2.4.0+):
   * FORTFACE_INSECURE_PROTOCOL, FORTFACE_GENERATE_DEVICE_REQUEST_INFO_ERROR,
   * FORTFACE_INTERNAL_SETUP_ERROR, FORTFACE_UNEXPECTED_START_ERROR.
   */
  private handleStartError(error: any) {
    console.error('Erro ao inicializar a Fortface SDK:', error);

    const errorCode = (error?.message || error?.code || error || '').toString();
    const matchedKey = Object.keys(START_ERROR_MESSAGES).find(key => errorCode.includes(key));
    const message = matchedKey
      ? START_ERROR_MESSAGES[matchedKey]
      : 'Erro ao inicializar a captura de biometria. Tente novamente.';

    this.captureError.emit(message);
  }

  fortfaceFinishSession = (fortfaceSessionResult: FortFaceSdk.IFortfaceSessionResult) => {
    const { action, data, sessionDetails } = fortfaceSessionResult;

    switch (action) {
      case 'capture':
        if (data) {
          this.handleResult(data);
        }
        break;
      case 'cancel':
        this.handleCancel();
        break;
      case 'timeout':
        this.handleTimeout();
        break;
      case 'timeout_ready':
        console.log("timeout_ready")
        break;
      case 'error':
        this.handleError(sessionDetails?.errorCode);
        break;
      default:
        break;
    }
  }


  handleResult(fortfaceData: FortFaceSdk.IFortfaceData) {
    const encryptData = fortfaceData.encryptData;
    const imageBase64 = fortfaceData.imgPreview
    if (!imageBase64) throw new Error("Image not found");

    // Log temporário para diagnóstico do payload da v2.4.3
    console.debug('[Fortface] encryptData keys:', encryptData ? Object.keys(encryptData) : null);

    // A partir da v2.4.3, o SDK pode retornar `imgBestFrame` em vez de `imgData`
    // (ambos são opcionais no tipo). Usamos qualquer um que esteja presente.
    const imgDataValue = encryptData?.imgData ?? (encryptData as any)?.imgBestFrame ?? "";

    const fortfaceDataResult: FortFaceData = {
      sessionId: this.sessionId,
      data: encryptData?.data ?? "",
      imgData: imgDataValue,
      key: encryptData?.key ?? "",
      imagePreview: imageBase64.replace('data:image/jpeg;base64,', ''),
    }

    this.imageCapture.emit(fortfaceDataResult);

  }

  handleCancel() {
    this.isLoading.emit(false);
    this.captureError.emit("Você cancelou a captura");
  }

  handleTimeout() {
    this.captureError.emit("Oopss! O tempo de captura esgotou, tente novamente");
  }

  handleError(errorCode?: FortFaceSdk.FortfaceSessionErrorCode | string) {
    const code = errorCode ? String(errorCode) : '';
    const message = SESSION_ERROR_MESSAGES[code] || (code ? `Erro na captura: ${code}` : 'Erro na captura');
    this.captureError.emit(message);
  }

}
